import axios from 'axios'; // 기본 axios 모듈에서 isAxiosError 가져오기
import axiosInstance from '../plugin/axios';

interface UploadResponse {
  file: string;
}

interface DriverSignupDTO {
  passid: string;
  password: string;
  phone: string;
  registration: string; // 업로드된 파일의 URL을 보낼 것으로 가정
}

interface CompanySignupDTO {
  passid: string;
  password: string;
  phone: string;
  registration: string;
  name: string;
  address: string;
  detail: string;
  charge: string;
  position: string;
}

interface SignupResponse {
  statusCode: number;
  message: string;
}

interface CorporationList {
  corporationId: number;
  name: string;
  address: string;
  detail: string;
}

export const uploadVehicleRegistration = async (
  file: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await axiosInstance.post<UploadResponse>(
      '/api/user/upload/registration/vehicle',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    // 서버가 반환하는 필드 이름이 다를 수 있으니 이 부분을 확인해야 합니다.
    if (response.data && response.data.file) {
      return response.data.file; // 서버가 반환하는 URL
    } else {
      throw new Error('Failed to upload file: No registration URL returned');
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Error: ${error.response.status}, ${error.response.data.message}`,
      );
    } else {
      throw new Error('Unexpected error occurred during file upload');
    }
  }
};

// 사업자 등록증 파일 전송
export const uploadCompanyRegistration = async (
  file: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await axiosInstance.post<UploadResponse>(
      '/api/user/upload/regsitration/company',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.file;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Error: ${error.response.status}, ${error.response.data.message}`,
      );
    } else {
      throw new Error('Unexpected error occurred during file upload');
    }
  }
};

export const driverSignup = async (
  data: DriverSignupDTO,
): Promise<SignupResponse> => {
  try {
    const response = await axiosInstance.post<SignupResponse>(
      '/api/user/auth/signup/driver',
      data,
    );
    return response.data;
  } catch (error) {
    // 에러 처리: 예외 발생 시 로그 또는 사용자에게 알림 등
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Error: ${error.response.status}, ${error.response.data.message}`,
      );
    } else {
      throw new Error('로그인 중 예상하지 못한 오류가 발생하였습니다.');
    }
  }
};

export const companySignup = async (
  data: CompanySignupDTO,
): Promise<SignupResponse> => {
  try {
    const response = await axiosInstance.post<SignupResponse>(
      '/api/user/auth/signup/company',
      data,
    );
    return response.data;
  } catch (error) {
    // 에러 처리: 예외 발생 시 로그 또는 사용자에게 알림 등
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        `Error: ${error.response.status}, ${error.response.data.message}`,
      );
    } else {
      throw new Error('로그인 중 예상하지 못한 오류가 발생하였습니다.');
    }
  }
};

// 아이디 중복 체크
export const checkPassid = async (passid: string): Promise<string> => {
  try {
    const response = await axiosInstance.get<{
      statusCode: number;
      message: string;
    }>(`/api/user/auth/check/${passid}`);
    return response.data.message;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw error;
    } else {
      throw new Error('Unexpected error occurred during ID check');
    }
  }
};

// 핸드폰 인증
// step 1
export const requestPhoneAuth = async (
  phone: string,
  userType: 'driver' | 'company',
): Promise<string> => {
  const response = await axiosInstance.post<{ uuid: string }>(
    '/api/user/cert/phone/step1',
    {
      phone,
      userType,
    },
  );
  return response.data.uuid;
};

// step 2

export const verifyPhoneAuth = async (
  uuid: string,
  code: string,
): Promise<string> => {
  const response = await axiosInstance.post<{
    statusCode: string;
    message: string;
  }>('/api/user/cert/phone/step2', {
    uuid,
    code,
  });
  return response.data.message;
};

// 아이디 찾기 인증
// step 1
export const requestPassidAuth = async (phone: string): Promise<string> => {
  const response = await axiosInstance.post<{ uuid: string }>(
    '/api/user/cert/passid/step1',
    {
      phone,
    },
  );
  return response.data.uuid;
};

// step 2

export const verifyPassidAuth = async (
  uuid: string,
  code: string,
): Promise<string> => {
  const response = await axiosInstance.post<{
    statusCode: string;
    passid: string;
  }>('/api/user/cert/passid/step2', {
    uuid,
    code,
  });
  return response.data.passid;
};

// 비밀번호 찾기 찾기 인증
// step 1
export const requestPasswordAuth = async (
  phone: string,
  passid: string,
): Promise<string> => {
  const response = await axiosInstance.post<{ uuid: string; passid: string }>(
    '/api/user/cert/password/step1',
    {
      phone,
      passid,
    },
  );
  return response.data.uuid;
};

// step 2

export const verifyPasswordAuth = async (
  uuid: string,
  code: string,
): Promise<string> => {
  const response = await axiosInstance.post<{
    statusCode: string;
    passid: string;
  }>('/api/user/cert/password/step2', {
    uuid,
    code,
  });
  return response.data.passid;
};

export const newPassowrdRegist = async (
  uuid: string,
  password: string,
): Promise<string> => {
  const response = await axiosInstance.put<{
    statusCode: string;
    message: string;
  }>('/api/user/cert/password/step3', {
    uuid,
    password,
  });
  return response.data.statusCode;
};

export const FetchCorporationList = async (): Promise<CorporationList[]> => {
  try {
    const response = await axiosInstance.get<CorporationList[]>(
      `/api/user/auth/signup/corporationlist`,
    );
    return response.data;
  } catch (error) {
    throw new Error();
  }
};
